// #region imports

// --- Angular
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

// --- RxJs
import { of } from 'rxjs';
import { catchError } from 'rxjs/operators';

// --- 3rd Party
import { retryBackoff } from 'backoff-rxjs';

// --- HS Code
import { OidcService } from '../account/oidc.service';
import { AppService } from '../app.service';
import { RegistrationValidationBaseService } from './registrationvalidation-base.service';
import {
  ApplicationError,
  ErrorMessage,
  IApplicationError,
  RegistrationTokenValidationModel,
  RegistrationValidationStages,
  TokenValidationErrorTypes,
} from '../../../models';
import { environment } from '../../../../environments/environment';

// #endregion

@Injectable({
  providedIn: 'root',
})
export class RegistrationValidationService extends RegistrationValidationBaseService {
  constructor(
    private http: HttpClient,
    appService: AppService,
    oidcService: OidcService
  ) {
    super(appService, oidcService);
  }

  validateRegistrationToken(id: string): void {
    if (!id) {
      return;
    }

    const url =
      environment.apiConfig.surveyApiUrl +
      environment.apiConfig.registrationValidationApi +
      `/${id}`;

    this.http
      .get<RegistrationTokenValidationModel>(url)
      .pipe(
        retryBackoff(environment.retryConfig),
        catchError(this._validationError.bind(this))
      )
      .subscribe((result) => {
        if (
          result.tokenValidationErrorType === TokenValidationErrorTypes.noError
        ) {
          this._authenticateFromOtac(result.otac);
          return;
        }
        this._evaluateError(result);
      });
  }

  private _evaluateError(result: RegistrationTokenValidationModel): void {

    let errorCode: number = ErrorMessage.commonInvalidTokenErrorCode;
    this._validationStage$.next(RegistrationValidationStages.tokenError);

    switch (result.tokenValidationErrorType) {
      case TokenValidationErrorTypes.invalidToken:
        break;
      case TokenValidationErrorTypes.tokenIsUsed:
        errorCode = ErrorMessage.commonUsedTokenErrorCode;
        break;
      case TokenValidationErrorTypes.tokenIsExpired:
        errorCode = ErrorMessage.commonExpiredTokenErrorCode;
        break;
      default:
        this._unexpectedError();
        break;
    }

    result.applicationError = ApplicationError.create(
      errorCode,
      'registrationValidationService',
      false,
    );
    result.applicationError.errorHeader = 'errorHeaderTokenError';

    this._validation$.next(result);
  }

  private _unexpectedError() {
    this._validationStage$.next(RegistrationValidationStages.tokenError);
    this._validation$.next({
      tokenValue: '',
      hasPassword: false,
      tokenValidationErrorType: TokenValidationErrorTypes.unexpectedError,
      locale: '',
      applicationError: {
        errorMessage: 'errorHeaderUnexpected',
        detailedMessage: 'commonUnexpectedError',
      },
    } as RegistrationTokenValidationModel);
  }

  private _validationError(err: any) {
    return of({
      tokenValue: '',
      hasPassword: false,
      tokenValidationErrorType: TokenValidationErrorTypes.unexpectedError,
      locale: '',
      applicationError: {
        errorMessage: 'errorHeaderUnexpected',
        detailedMessage: 'commonUnexpectedError',
      },
    } as RegistrationTokenValidationModel);
  }
}
